<template lang="pug">
  #challenges-page
    WtTile(ancestor vertical)
      WtTile(parent)
        WtTile(child child-class="")
          .level.challenges-header.is-mobile
            .level-left
              .level-item
                WtTitle(h1) Challenges
            .level-right
              .level-item
                WtButton(v-if="canCreateChallenge"
                  color="primary"
                  isRouterLink
                  :to="{ name: isAdmin ? 'ChallengeCenterBuilder' : 'MemberChallengeBuilder', params: { mode: 'member' } }"
                ) {{ isMobile ? "CREATE NEW" : "CREATE A NEW CHALLENGE" }}
      BCarousel#featured-challenges-carousel(
        v-if="validFeaturedChallenges.length"
        :indicator-inside="false"
      )
        BCarouselItem(
          v-for="(featuredChallenge, i) in validFeaturedChallenges"
          :key="i"
        )
          FeaturedChallengeLarge(
            v-if="!_.isEmpty(featuredChallenge)"
            :featured-challenge="featuredChallenge"
          )
        template(#indicators="props")
          BImage(
            class="al image"
            :src="getChallengeImage(validFeaturedChallenges[props.i])"
            @error="replaceWithDefaultImage"
          )

      WtTile(parent)
        WtTile(child :is-loading="!memberChallengesLoaded")
          MemberChallengesTable(
            :challenges-data="validMemberChallenges"
            :loading="!memberChallengesLoaded"
          )

      WtTile(parent)
        WtTile(child :is-loading="!programChallengesLoaded")
          ProgramChallengesTable(
            :challenges-data="programChallenges"
            :loading="!programChallengesLoaded"
          )

      WtTile(parent v-if="programChallengesLoaded && memberChallengesLoaded && !_.isEmpty(_.get(archivedChallenges, 'data', []))")
        WtTile(child :is-loading="!archivedChallengesLoaded")
          MemberChallengesArchiveTable(
            :archived-challenges-object="archivedChallenges"
            :fetch-action="fetchArchived"
            :loading="!archivedChallengesLoaded"
          )
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import WtButton from "@@/WtButton/WtButton";
import ProgramChallengesTable from "@@/Challenges/ProgramChallengesTable";
import MemberChallengesTable from "@@/Challenges/MemberChallengesTable";
import FeaturedChallengeLarge from "@@/Challenges/FeaturedChallengeLarge";
import { fetchAllMixin } from "@/mixins/fetchAllMixin";
import MemberChallengesArchiveTable from "@@/Challenges/MemberChallengesArchiveTable";
import { challengeMixin } from "@/mixins/challengeMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";

export default {
  name: "Challenges",

  components: {
    MemberChallengesArchiveTable,
    WtTitle,
    WtTile,
    WtButton,
    ProgramChallengesTable,
    MemberChallengesTable,
    FeaturedChallengeLarge
  },

  mixins: [analyticsTrackingMixin, fetchAllMixin, challengeMixin],

  data() {
    return {
      page: `Challenges`,
      programChallengesData: [],
      programChallengesEmpty: true,
      memberChallengesEmpty: true
    };
  },

  computed: {
    ...mapGetters(["isMobile"]),
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("pages", ["isPageLoaded", "isPageLoading"]),
    ...mapGetters("members", [
      "archivedChallenges",
      "archivedChallengesLoaded"
    ]),
    ...mapState("members", {
      memberChallenges: state => state.challenges,
      memberChallengesLoaded: state => state.challengesLoaded
    }),
    // TODO rename state.challenges to state.programChallenges and state.programChallengesLoaded
    ...mapState("program", {
      programChallenges: state => state.challenges,
      programChallengesLoaded: state => state.challengesLoaded
    }),
    ...mapGetters("program", [
      "featuredChallenges",
      "currentFeaturedChallengeLoaded"
    ]),
    ...mapGetters(["members/challengeInvites"]),

    fullPageLoader() {
      // since there is no intial state for 'isPageLoading(this.page)'
      if (this._.isUndefined(this.isPageLoading(this.page))) {
        return true;
      }
      return this.isPageLoading(this.page);
    },

    canCreateChallenge() {
      if (
        !this.$auth.user().program.memberChallengeCreation &&
        !this.$auth.user().isAdmin
      ) {
        return false;
      } else {
        return true;
      }
    },
    // check that challenge is public or member is invited and challenge didn't end
    validFeaturedChallenges() {
      return this.featuredChallenges.filter(challenge => {
        return (
          (this.isPublic(challenge) ||
            (!this.isPublic(challenge) && this.isInvited(challenge))) &&
          !this.isTodayAfterChallengeEnd(challenge)
        );
      });
    },
    // check that challenges didn't end
    validMemberChallenges() {
      return this.memberChallenges.filter(challenge => {
        return !challenge.isCompleted;
      });
    }
  },

  async created() {
    this.unsubscribeDeleteChallenge = this.$store.subscribe(async mutation => {
      if (mutation.type === "challenges/DELETE_CHALLENGE") {
        await this.toFetchOrNotToFetch(this, this.page, {
          force: true
        });
      }
    });

    await this.toFetchOrNotToFetch(this, this.page);
  },

  beforeDestroy() {
    this.unsubscribeDeleteChallenge();
  },

  mounted() {
    this.trackFeatureView("challenge", "challenge_list");
  },

  methods: {
    ...mapActions("pages", [
      "setPageLoaded",
      "setPageLoading",
      "fetchAllPageChallenges"
    ]),
    ...mapActions("members", ["fetchArchivedChallenges"]),

    async fetchAllPage(vm) {
      const member = vm.$auth.user();

      await vm.fetchAllPageChallenges(member);
    },

    fetchArchived(payload) {
      Object.assign(payload, {
        memberId: this.$auth.user().id
      });

      return this.fetchArchivedChallenges(payload);
    },

    replaceWithDefaultImage(event) {
      event.target.src = this.mapImagePlaceholder();
    },

    isPublic(challenge) {
      return challenge.public === 1;
    },

    isDenied(challenge) {
      const memberDeniedInviteIds = this["members/challengeInvites"]
        .filter(invite => invite.denied)
        .map(invite => invite.inviteId);
      return memberDeniedInviteIds.includes(challenge.id);
    },

    isInvited(challenge) {
      const memberInviteIds = this["members/challengeInvites"].map(
        invite => invite.inviteId
      );

      return (
        memberInviteIds.includes(challenge.id) ||
        this.$auth.user().id === challenge.initiator.id
      );
    },

    getChallengeImage(challenge) {
      // Always try to get map.image with the placeholder as fallback
      const mapImage = this._.get(
        challenge,
        "map.image",
        this.mapImagePlaceholder()
      );

      // Check if challenge is preventative (for custom image)
      const isPreventative = this._.get(challenge, "isPreventative", false);

      return isPreventative
        ? this._.get(challenge, "challengeImage", null) || mapImage
        : mapImage;
    }
  }
};
</script>

<style lang="sass" scoped>
#challenges-page
  .challenges-header
    width: 100%
    +mobile
      margin: 23px 0 23px 0
  #featured-challenges-carousel
    /deep/ .indicator-item
      width: 50px
      height: 50px
      .al
        height: 100%
      img
        border-radius: 50%
        filter: brightness(50%)
        border: 2px solid transparent
        padding: 5px
        object-fit: cover
        height: 100%
      &.is-active
        img
          filter: brightness(1)
          border: 2px solid $navy-dark
    /deep/ .carousel-arrow.is-hovered
      z-index: 0 !important
</style>
